import React, { useState } from "react";
import { CloseIcon } from "icons/close/CloseIcon";
import { SuccessPaymentIcon } from "icons/payments/successPayment";
import InputField from "components/form/InputField";
import Button from "components/form/Button";
import { formatCurrency, validateEmail } from "utils/HelperFunctions";
import { useTid } from "utils/testid";

interface SuccessRetryPaymentProps {
  failedPayment: number;
  emailDisplay: string;
  paymentScheduleId: string;
  isEmailLoading: boolean;
  isDownloadLoading: boolean;
  onClose: () => void;
  onNavigateDashboard: () => void;
  onViewDetails: () => void;
  onSendEmail: (email: string) => Promise<void>;
  onDownload: (invoiceId: string) => Promise<void>;
}

const SuccessRetryPayment: React.FC<SuccessRetryPaymentProps> = ({
  failedPayment,
  emailDisplay,
  paymentScheduleId,
  isEmailLoading,
  isDownloadLoading,
  onClose,
  onNavigateDashboard,
  onViewDetails,
  onSendEmail,
  onDownload,
}) => {
  const tid = useTid();
  const [emailInput, setEmailInput] = useState<string>("");
  const [isErr, setIsErr] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string | undefined>(undefined);

  const handleSendEmail = async () => {
    if (!validateEmail(emailInput)) {
      setIsErr(true);
      setErrMsg("Please enter a valid email address");
      return;
    }
    await onSendEmail(emailInput);
  };

  return (
    <div
      className="w-full h-full "
      data-testid={tid("update-plan-success-retry")}
      aria-label="Payment plan retry updated successfully"
    >
      <div className="flex justify-end">
        <div
          onClick={() => onClose()}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onClose();
            }
          }}
          className="cursor-pointer p-2 rounded-full hover:!bg-bgSecondary"
          role="button"
          tabIndex={0}
          data-testid={tid("update-plan-success-retry-close-button")}
          aria-label="Close successful retry payment plan update"
        >
          <CloseIcon aria-hidden="true" />
        </div>
      </div>
      <div className="lg:px-0 px-5 grid place-items-center ">
        <div className="flex flex-col justify-center items-center max-w-[630px] mt-[7.5%] gap-24">
          <div className="w-full flex flex-col justify-center items-center">
            <SuccessPaymentIcon aria-hidden="true" />
            <p
              className="lg:text-2xl text-xl text-txtPrimary font-medium mt-10"
              data-testid={tid("update-plan-success-retry-title")}
            >
              Payment plan updated successfully
            </p>
            <div
              className="w-full text-center bg-bgThird lg:py-5 lg:px-2 py-4 px-3 lg:text-xl text-base text-txtParagraph font-normal mt-10"
              data-testid={tid("update-plan-success-retry-payment-message")}
            >
              Your payment of{" "}
              <span className="font-bold">
                {formatCurrency(failedPayment) || "$0.00"}
              </span>{" "}
              has been received.
            </div>

            <p className="lg:text-base text-sm font-normal text-txtParagraph mt-4">
              <span
                className="text-btnPrimaryActive hover:!text-btnPrimaryHover  underline cursor-pointer"
                onClick={onViewDetails}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onViewDetails();
                  }
                }}
                role="button"
                tabIndex={0}
                data-testid={tid("update-plan-success-retry-view-details-link")}
                aria-label="View full plan details"
              >
                Click here
              </span>{" "}
              to view your full plan details.
            </p>
          </div>
          <div className="w-full flex flex-col justify-center items-center">
            <div className="flex-col flex justify-center items-center lg:gap-[30px] gap-5 w-full">
              <p className="lg:text-base text-sm text-center text-txtSecondary lg:px-5">
                A copy of this receipt will automatically be sent to{" "}
                <span className="font-bold">{emailDisplay}.</span> To send a
                copy to an additional email, enter the address below.
              </p>
              <div className="px-[10px] w-full">
                <InputField
                  type="email"
                  name="email"
                  label=""
                  placeholder="Enter your email address"
                  value={emailInput}
                  onChange={(e) => {
                    setEmailInput(e.target.value);
                    setIsErr(false);
                    setErrMsg(undefined);
                  }}
                  className="!py-3"
                  error={errMsg}
                  isErr={isErr}
                  data-testid={tid("update-plan-success-retry-email-input")}
                  aria-label="Additional receipt email"
                />
              </div>
              <div className="m-[10px] w-full flex justify-center items-center gap-5 px-[10px]">
                <Button
                  text="Send Email"
                  className="w-full"
                  isLoading={isEmailLoading}
                  onClick={handleSendEmail}
                  isDisabled={emailInput.length === 0}
                  data-testid={tid(
                    "update-plan-success-retry-send-email-button"
                  )}
                  aria-label="Send receipt email"
                />
                <Button
                  text="Download"
                  className="w-full"
                  isLoading={isDownloadLoading}
                  onClick={() => onDownload(paymentScheduleId)}
                  data-testid={tid("update-plan-success-retry-download-button")}
                  aria-label="Download receipt"
                />
              </div>
            </div>
            <div className="w-full mt-6  lg:text-base text-sm text-center text-btnPrimaryActive hover:!text-btnPrimaryHover ">
              <div
                className="cursor-pointer"
                onClick={onNavigateDashboard}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onNavigateDashboard();
                  }
                }}
                role="button"
                tabIndex={0}
                data-testid={tid("update-plan-success-retry-dashboard-link")}
                aria-label="Back to dashboard"
              >
                Back to dashboard
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuccessRetryPayment;
